// @flow
import { RESORT_SEO } from 'Client/cms/toolkit/constants';
import { resortAction, messageFormat } from '@clubmed/utils';
import graphQLClient from 'Utils/graphql-client/graphqlClient';
import { LINE_PRODUCT_SEO_TITLE } from 'Utils/request/requestDictionary';

type CMSProps = {
  config: {
    resort: {
      seo: {
        priceCondition: string,
      },
    },
  },
  locale: string,
  origin: string,
  resort: {
    description: string,
    htmlHeader: {
      meta: $ReadOnlyArray<{ content: string, key?: string, name?: string, property?: string }>,
    },
    images: Array<string>,
    name: string,
    price: {
      bestPrice?: FormattedPrice,
      initialPrice?: FormattedPrice,
      termsAndConditions: { duration: number, numberOfAdults: number },
      unit: string,
    } | null,
    title: string,
    tripadvisor: { numReviews: number, rating: number },
  },
  resortId: string,
  title: string,
};

const getPriceRange = ({ price, priceConditionLabel }) => {
  if (!price || (!price.bestPrice && !price.initialPrice)) {
    return null;
  }

  const {
    bestPrice: { formattedValue: bestPrice } = {},
    initialPrice: { formattedValue: initialPrice } = {},
    termsAndConditions: { duration, numberOfAdults },
    unit,
  } = price;

  const priceToUse = bestPrice || initialPrice;

  return priceToUse
    ? messageFormat(priceConditionLabel).format({
        bestPrice: priceToUse,
        duration,
        numberOfAdults,
        priceUnit: unit.toLowerCase(),
      })
    : null;
};

const getStructuredData = (props: CMSProps) => {
  const {
    origin,
    resort: {
      name,
      images,
      description,
      price,
      tripadvisor: { numReviews, rating },
    },
    config: {
      resort: {
        seo: { priceCondition },
      },
    },
  } = props;

  const priceRange = getPriceRange({ price, priceConditionLabel: priceCondition });

  const structuredData = {
    aggregateRating: {
      ratingValue: rating,
      reviewCount: numReviews,
      bestRating: '5',
      '@context': 'http://schema.org/',
      '@type': 'AggregateRating',
    },
    description,
    image: images,
    name,
    ...(priceRange ? { priceRange } : null),
    url: origin,
    '@context': 'http://schema.org/',
    '@type': 'Hotel',
  };

  return {
    type: 'application/ld+json',
    innerHTML: JSON.stringify(structuredData),
  };
};

const getOpenGraphData = (props: CMSProps) => {
  return [
    { property: 'og:locale', content: props.locale },
    { property: 'og:type', content: 'website' },
    { property: 'og:title', content: props.resort.title },
    { property: 'og:description', content: props.resort.description },
    { property: 'og:url', content: `${props.origin}` },
    { property: 'og:site_name', content: 'Club Med' },
    ...props.resort.images.map(image => ({
      key: `og:image${image}`,
      property: 'og:image',
      content: image,
    })),
  ];
};

export const productSEOTitleResolver = {
  name: RESORT_SEO,
  props() {
    return {
      title: (props: CMSProps) =>
        resortAction.generateResortTitle(props.title || '', props.resort.name),
      meta: (props: CMSProps) => [...props.resort.htmlHeader.meta, ...getOpenGraphData(props)],
      script: (props: CMSProps) => [getStructuredData(props)],
    };
  },
  resolve: (appContext: AppContext = {}, props: CMSProps) => {
    const query = `query {
      resort(resortId: "${props.resortId}") {
        name
        htmlHeader { meta {name content} }
        images
        description
        title
        tripadvisor(resortId: "${props.resortId}") {
          rating
          numReviews
        }
        price(resortId: "${props.resortId}") {
          bestPrice {
            formattedValue
          }
          initialPrice {
            formattedValue
          }
          termsAndConditions {
            duration
            numberOfAdults
          }
          unit
        }
      }
      config {
        resort {
          seo {
            priceCondition
          }
        }
      }
    }`;
    return graphQLClient
      .runQuery(query, appContext, { requestName: LINE_PRODUCT_SEO_TITLE, strictErrors: false })
      .then(data => ({
        origin: appContext.originalUrl,
        locale: appContext.locale,
        ...data,
      }));
  },
};
